'use client';

import type { ReactNode } from 'react';
import {
  Box,
  Toaster as ChakraToaster,
  Portal,
  Spinner,
  Stack,
  Toast,
  createToaster,
  useSlotRecipe,
} from '@chakra-ui/react';
import { toastSlotRecipe } from '../../theme/recipes/toast';

/** Optional extras for richer toasts (thumb stacks, custom body, etc.). */
export type RiftboundToastMeta = {
  /** Replaces the status indicator / spinner when set. */
  leading?: ReactNode;
  /** Extra content below title/description (inside the text column). */
  body?: ReactNode;
  /** When true, omit the default status indicator (even without `leading`). */
  hideIndicator?: boolean;
  /** Stretch toast width for richer layouts (e.g. just-scanned). */
  wide?: boolean;
};

/**
 * A toast stays only when the result is invisible where the person acted,
 * they still need to hear about it, and it can disappear on its own.
 * Undo and off-screen write failures belong here. A save, publish, copy,
 * or navigation that the screen already shows does not.
 */
export const riftboundToaster = createToaster({
  placement: 'bottom-end',
  pauseOnPageIdle: true,
  // Clear the mobile tab bar + safe area; harmless extra space on desktop.
  offsets: {
    top: '16px',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
    right: '16px',
    left: '16px',
  },
});

function toastMeta(toast: { meta?: unknown }): RiftboundToastMeta {
  return (toast.meta && typeof toast.meta === 'object' ? toast.meta : {}) as RiftboundToastMeta;
}

export function RiftboundToaster() {
  const recipe = useSlotRecipe({ recipe: toastSlotRecipe });
  const chrome = recipe({});
  return (
    <Portal>
      <ChakraToaster toaster={riftboundToaster} insetInline={{ mdDown: '4' }}>
        {(toast) => {
          const meta = toastMeta(toast);
          const showDefaultIndicator = !meta.hideIndicator && meta.leading == null;

          return (
            <Toast.Root css={chrome.root} width={{ base: '100%', md: meta.wide ? 'sm' : 'sm' }} maxW="360px">
              {meta.leading ? (
                <Box flexShrink={0} alignSelf="center" overflow="visible" py={0.5} pr={1}>
                  {meta.leading}
                </Box>
              ) : showDefaultIndicator ? (
                toast.type === 'loading' ? (
                  <Spinner size="sm" color="blue.solid" />
                ) : (
                  <Toast.Indicator />
                )
              ) : null}
              <Stack gap="1" flex="1" maxWidth="100%" minW={0}>
                {toast.title && <Toast.Title css={chrome.title}>{toast.title}</Toast.Title>}
                {toast.description && (
                  <Toast.Description css={chrome.description}>{toast.description}</Toast.Description>
                )}
                {meta.body}
              </Stack>
              {toast.action && (
                <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
              )}
              {toast.closable && <Toast.CloseTrigger />}
            </Toast.Root>
          );
        }}
      </ChakraToaster>
    </Portal>
  );
}
